import { useCallback, useId, useRef, useState } from 'react';

import { XMarkIcon } from '@heroicons/react/20/solid';

export type ChipState = 'valid' | 'invalid' | 'warning';

export type ChipValidationResult = {
  state: ChipState;
  message?: string;
};

export type ChipValidatorContext = {
  values: string[];
  index: number;
};

export type ChipValidator = (value: string, ctx?: ChipValidatorContext) => ChipState | ChipValidationResult;

export type ChipInputProps = {
  value: string[];
  onChange: (next: string[]) => void;
  delimiters?: RegExp;
  tokenizeOnBlur?: boolean;
  // Duplicates aren't dropped — callers flag them by checking ctx.values, so the
  // user sees the rejection (e.g. red "Duplicate" chip) instead of silent loss.
  validate?: ChipValidator;
  normalize?: (value: string) => string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  // Tokens beyond the cap are silently dropped — caller surfaces overflow as a banner.
  maxChips?: number;
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'onKeyDown' | 'onBlur' | 'onPaste' | 'disabled' | 'placeholder' | 'id'
  >;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

const DEFAULT_DELIMITERS = /[\s,;]+/;

// Mirrors Blossom Badge variants: default_filled (no border), warning, danger — size sm.
// Transparent border on `valid` keeps row alignment with the bordered variants.
const stateClasses: Record<ChipState, string> = {
  valid: 'border border-gray-100 bg-gray-100 text-gray-700',
  warning: 'border border-amber-200 bg-amber-100 text-amber-900',
  invalid: 'border border-red-200 bg-red-100 text-red-900',
};

function resolveValidation(
  value: string,
  validate: ChipValidator | undefined,
  ctx: ChipValidatorContext,
): ChipValidationResult {
  if (!validate) return { state: 'valid' };
  const result = validate(value, ctx);
  return typeof result === 'string' ? { state: result } : result;
}

export function ChipInput({
  value,
  onChange,
  delimiters = DEFAULT_DELIMITERS,
  tokenizeOnBlur = true,
  validate,
  normalize,
  id,
  placeholder,
  disabled,
  maxChips,
  inputProps,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: ChipInputProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const commit = useCallback(
    (rawTokens: string[]) => {
      if (rawTokens.length === 0) return;
      const next = [...value];
      let changed = false;
      for (const raw of rawTokens) {
        const candidate = normalize ? normalize(raw) : raw.trim();
        if (!candidate) continue;
        if (typeof maxChips === 'number' && next.length >= maxChips) break;
        // Duplicates are NOT dropped here — the caller's `validate` decides what
        // happens to them. Silent drops surprise the user; flagging via the
        // validator (e.g., red "Duplicate" chip) makes the rejection visible.
        next.push(candidate);
        changed = true;
      }
      if (changed) onChange(next);
    },
    [value, onChange, normalize, maxChips],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    if (!delimiters.test(text)) {
      setInputValue(text);
      return;
    }
    // Split on delimiters; everything before the trailing fragment becomes chips,
    // the trailing fragment stays in the input so the user can keep typing.
    const parts = text.split(delimiters);
    const trailing = parts.pop() ?? '';
    commit(parts);
    setInputValue(trailing);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (inputValue) {
        commit([inputValue]);
        setInputValue('');
      }
      return;
    }
    // Pop the last chip back into the input — lets the user fix a typo without
    // having to retype the whole thing.
    if (event.key === 'Backspace' && inputValue === '' && value.length > 0) {
      event.preventDefault();
      const last = value[value.length - 1] ?? '';
      onChange(value.slice(0, -1));
      setInputValue(last);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text');
    if (!delimiters.test(pasted)) return; // single token — let the default paste land in the input
    event.preventDefault();
    const tokens = pasted.split(delimiters);
    // If the user had partial text typed, prepend it to the first pasted token
    // so we don't lose it.
    if (tokens.length > 0 && inputValue) {
      tokens[0] = inputValue + tokens[0];
    }
    const trailing = tokens.pop() ?? '';
    commit(tokens);
    setInputValue(trailing);
  };

  const handleBlur = () => {
    // Switching tabs or apps also blurs the input, often mid-address while the user copies the
    // rest of it. Only leaving the field within the page counts as finishing the entry.
    if (tokenizeOnBlur && inputValue && document.hasFocus()) {
      commit([inputValue]);
      setInputValue('');
    }
  };

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  // Puts an invalid chip's text back in the input so the user can fix it instead of retyping it.
  const editAt = (index: number) => {
    const next = value.filter((_, i) => i !== index);
    const pending = normalize ? normalize(inputValue) : inputValue.trim();
    onChange(pending ? [...next, pending] : next);
    setInputValue(value[index] ?? '');
    inputRef.current?.focus();
  };

  return (
    <div
      className={`flex min-h-[88px] w-full cursor-text flex-wrap content-start items-start gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-wt-primary ${
        disabled ? 'cursor-not-allowed opacity-60' : ''
      } ${className ?? ''}`}
    >
      {value.map((chip, idx) => {
        const validation = resolveValidation(chip, validate, { values: value, index: idx });
        return (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: chip values aren't guaranteed unique pre-dedupe
            key={`${chip}-${idx}`}
            title={validation.message}
            className={`shadow-xs inline-flex items-center gap-x-1 rounded px-1.5 py-0.5 text-xs font-medium ${stateClasses[validation.state]}`}
          >
            {validation.state === 'invalid' ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  editAt(idx);
                }}
                disabled={disabled}
                className="cursor-text underline decoration-dotted underline-offset-2"
                aria-label={`Edit ${chip}`}
              >
                {chip}
              </button>
            ) : (
              <span>{chip}</span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeAt(idx);
              }}
              disabled={disabled}
              className="-mr-1 inline-flex h-4 w-4 items-center justify-center rounded text-current opacity-70 hover:opacity-100"
              aria-label={`Remove ${chip}`}
            >
              <XMarkIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        );
      })}
      {/*
        The `!` important prefix on size + padding + ring properties defeats the
        global `@tailwindcss/forms` reset (enabled in orchid-v2's `tailwind.config.js`)
        that pins every `[type="text"]` to `padding: 8/12; font-size: 16px` and
        adds a focus ring/border via the same global selector. Without !important
        the input would render at 16px and the placeholder would shift on focus.
      */}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ''}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className="!min-w-[140px] !flex-1 !rounded-none !border-none !bg-transparent !p-0 !text-sm !leading-6 !text-gray-900 !shadow-none !outline-none !ring-0 placeholder:text-gray-400"
        {...inputProps}
      />
    </div>
  );
}
